/**
 * Quotas API — contract #12–#15 (docs/05-dev-spec.md §4, task T.C4b).
 *
 *   #12 GET  /api/quotas                        → `{ data: [QuotaDto + venueName + programName] }`
 *   #13 PUT  /api/quotas/:venueId/:courseId     → `{ data: QuotaDto }`   (ADMIN, upsert)
 *   #14 POST /api/quotas/transfers              → `{ data: TransferDto }` (ADMIN, atomic)
 *   #15 GET  /api/quotas/transfers              → `{ data: [TransferDto] }`
 *
 * The shapes mirror the REAL api module (`apps/api/src/quotas/dto.ts`), which
 * mirrors the generated `packages/shared/src/openapi/api-types.ts` 1:1 — the
 * generated file is not reachable from the bundle (`@cb-manager/shared` exports
 * only its barrel), so the mirror lives here, as `applications.ts` does for #3-#5.
 *
 * ONE divergence is mirrored the way the api mirrors it: the generated
 * `TransferDto` types `format` as required, while the schema, the doc comment and
 * the `QuotaTransfer` table all say "`courseId`/`format` null = venue-level
 * transfer". A venue-level row has no format to invent, so the wire type is
 * `CourseFormat | null` here too.
 *
 * Error semantics the dialog surfaces verbatim (the api's own rules):
 *   #13/#14 below ADMIN            → 403 FORBIDDEN (the UI hides the controls instead)
 *   #14 unknown venue / course id  → 400 VALIDATION (a BODY reference, not a path resource)
 *   #14 donor plan < amount        → 409 CONFLICT  («plan insufficient: …»)
 *   #13 unknown venueId/courseId   → 404 NOT_FOUND (path resources)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { VENUE_PLANS_ROOT } from './venue-plans';

import { buildQuery, requestEnvelope, type Envelope } from './client';

export type CourseFormat = 'OFFLINE' | 'ONLINE';

/** The canonical latin vocabulary of docs/05 §4 (sources are normalised at ingest). */
export const COURSE_FORMATS: readonly CourseFormat[] = ['OFFLINE', 'ONLINE'];

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** The frozen field set {id, venueId, courseId, format, plan} of #13. */
export interface QuotaDto {
  id: number;
  venueId: number;
  courseId: number;
  format: CourseFormat;
  plan: number;
}

/**
 * A #12 row: `QuotaDto` plus the two names the grid prints. ADDITIVE per the
 * T1.0 ratification note — a grid without a program title is unreadable.
 */
export interface QuotaRowDto extends QuotaDto {
  /** `Venue.name` — the master dictionary name. */
  venueName: string;
  /** `Program.name` — the «Наименование ДОП» title. */
  programName: string;
}

/** #14/#15 — 1:1 with `QuotaTransfer`; both `courseId` and `format` null = venue-level. */
export interface TransferDto {
  id: number;
  fromVenueId: number;
  toVenueId: number;
  /** Null on a venue-level transfer. */
  courseId: number | null;
  /** Null on a venue-level transfer. */
  format: CourseFormat | null;
  amount: number;
  reason: string;
  /** `AppUser.id` of the admin who signed the move. */
  actorUserId: number;
  /** ISO-8601. */
  createdAt: string;
}

/** Body of #13 — exactly these two keys (the api rejects anything else with 400). */
export interface QuotaUpsertInput {
  format: CourseFormat;
  /** Non-negative integer; 0 is a legal «no seats here» row. */
  plan: number;
}

/**
 * Body of #14. `courseId`+`format` travel TOGETHER (an exact transfer) or are
 * BOTH absent (venue-level, applied pro-rata to the donor's plans) — a
 * half-specified scope has no semantics and the api answers 400.
 */
export interface TransferCreateInput {
  fromVenueId: number;
  toVenueId: number;
  amount: number;
  courseId?: number;
  format?: CourseFormat;
  reason: string;
}

export type QuotaListEnvelope = Envelope<QuotaRowDto[]>;
export type TransferListEnvelope = Envelope<TransferDto[]>;

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/** The query #12 accepts (`venueId`, `courseId` — both server-side). */
export interface QuotaListQuery {
  venueId?: number;
  courseId?: number;
}

/** The query #15 accepts (`venueId` matches EITHER direction of a transfer). */
export interface TransferListQuery {
  venueId?: number;
}

export function quotasPath(query: QuotaListQuery = {}): string {
  return `/quotas${buildQuery(query)}`;
}

export function quotaUpsertPath(venueId: number, courseId: number): string {
  return `/quotas/${encodeURIComponent(venueId)}/${encodeURIComponent(courseId)}`;
}

export function transfersPath(query: TransferListQuery = {}): string {
  return `/quotas/transfers${buildQuery(query)}`;
}

export async function fetchQuotas(query: QuotaListQuery, signal?: AbortSignal): Promise<QuotaListEnvelope> {
  return requestEnvelope<QuotaRowDto[]>('GET', quotasPath(query), ['data'], { signal });
}

export async function fetchTransfers(query: TransferListQuery, signal?: AbortSignal): Promise<TransferListEnvelope> {
  return requestEnvelope<TransferDto[]>('GET', transfersPath(query), ['data'], { signal });
}

export async function putQuota(
  venueId: number,
  courseId: number,
  body: QuotaUpsertInput,
  signal?: AbortSignal,
): Promise<QuotaDto> {
  const envelope = await requestEnvelope<QuotaDto>('PUT', quotaUpsertPath(venueId, courseId), ['data'], {
    // `method` is set in BOTH places on purpose: `apiRequest` spreads `init`
    // into the fetch call and currently never forwards its own `method`
    // argument (see `applications.ts` for the same note).
    method: 'PUT',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return envelope.data;
}

export async function postTransfer(body: TransferCreateInput, signal?: AbortSignal): Promise<TransferDto> {
  const envelope = await requestEnvelope<TransferDto>('POST', transfersPath(), ['data'], {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return envelope.data;
}

// ---------------------------------------------------------------------------
// Cache identity + hooks
// ---------------------------------------------------------------------------

/** Root of every grid cache entry — the optimistic upsert patches them all. */
export const QUOTA_GRID_ROOT = ['quotas', 'grid'] as const;

/** Cache identity of one scoped grid (the venue/course filters are part of it). */
export function quotasQueryKey(query: QuotaListQuery): readonly [string, string, QuotaListQuery] {
  return ['quotas', 'grid', query];
}

export const TRANSFER_ROOT = ['quotas', 'transfers'] as const;

export function transfersQueryKey(query: TransferListQuery): readonly [string, string, TransferListQuery] {
  return ['quotas', 'transfers', query];
}

/**
 * #12 — the quota grid. Two entries are kept side by side, exactly like the
 * dashboard's region pair: the unfiltered one feeds the venue/course pickers (a
 * scoped response only knows its own slice) and the scoped one drives the table.
 */
export function useQuotas(query: QuotaListQuery) {
  return useQuery({
    queryKey: quotasQueryKey(query),
    queryFn: ({ signal }) => fetchQuotas(query, signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}

/** #15 — the ledger, newest first, `venueId` matching either direction. */
export function useTransfers(query: TransferListQuery) {
  return useQuery({
    queryKey: transfersQueryKey(query),
    queryFn: ({ signal }) => fetchTransfers(query, signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}

/**
 * #13 — the inline plan edit, OPTIMISTIC: the cell shows the typed value the
 * moment it is committed, every scoped grid entry is patched (the unfiltered
 * one included, or the pickers would lag one render behind), and a failure
 * rolls everything back and hands the api's message to the page (403/404/400).
 */
export function useQuotaUpsert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { venueId: number; courseId: number; body: QuotaUpsertInput }) =>
      putQuota(input.venueId, input.courseId, input.body),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: QUOTA_GRID_ROOT });
      const entries = queryClient.getQueriesData<QuotaListEnvelope>({ queryKey: QUOTA_GRID_ROOT });
      for (const [key, value] of entries) {
        if (!value) continue;
        queryClient.setQueryData<QuotaListEnvelope>(key, {
          ...value,
          data: applyPlanToGrid(value.data, input.venueId, input.courseId, input.body),
        });
      }
      return { entries };
    },
    onError: (_error, _input, context) => {
      for (const [key, value] of context?.entries ?? []) {
        queryClient.setQueryData(key, value);
      }
    },
    onSettled: () => {
      // The server's answer is the authority — a created row arrives with its
      // id, and the ledger-adjacent views re-read from the same refetch.
      void queryClient.invalidateQueries({ queryKey: QUOTA_GRID_ROOT });
    },
  });
}

/**
 * #14 — the signed move. The ledger row and BOTH plan changes land in one
 * transaction, so a success invalidates the grid and the ledger together.
 */
export function useTransferCreate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: TransferCreateInput) => postTransfer(body),
    onSuccess: () => {
      // T.V1 — the grid reads the venue plans now, and a move changes exactly those.
      void queryClient.invalidateQueries({ queryKey: VENUE_PLANS_ROOT });
      // The legacy per-programme grid is deprecated but still on the wire.
      void queryClient.invalidateQueries({ queryKey: QUOTA_GRID_ROOT });
      void queryClient.invalidateQueries({ queryKey: TRANSFER_ROOT });
      // …and the dashboard/venues aggregates sum the same plans.
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * The optimistic half of #13: only the matching (venue, course, format) row
 * moves; a row the PUT is about to CREATE has no cell yet, so it is appended
 * provisionally (id `-1`, names blank) and the refetch replaces it with the
 * server's row. Pure, and the page never renders a stale plan.
 */
export function applyPlanToGrid(
  rows: QuotaRowDto[],
  venueId: number,
  courseId: number,
  body: QuotaUpsertInput,
): QuotaRowDto[] {
  const index = rows.findIndex(
    (row) => row.venueId === venueId && row.courseId === courseId && row.format === body.format,
  );
  if (index === -1) {
    return [
      ...rows,
      { id: -1, venueId, courseId, format: body.format, plan: body.plan, venueName: '', programName: '' },
    ];
  }
  return rows.map((row, position) =>
    position === index ? { ...row, plan: body.plan } : row,
  );
}
